import React, { useEffect, useMemo, useState } from "react";
import {
  alpha,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { api } from "../api/axios";
import { IconTile } from "./ui";
import { buildEmbedSnippet, needsInstallUpgrade, widgetServerUrl } from "../utils/embedSnippet";

const SNOOZE_KEY = "aw_install_upgrade_snooze_until";
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000;

function primaryDomain(site) {
  const d = String(site?.domain || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean)[0];
  if (!d) return "";
  return /^https?:\/\//i.test(d) ? d.replace(/\/$/, "") : `https://${d}`;
}

function readSnooze() {
  try {
    return Number(window.localStorage.getItem(SNOOZE_KEY) || 0);
  } catch {
    return 0;
  }
}

/**
 * Tells owners of sites that still use a legacy (render-blocking) installation to upgrade,
 * with a guided one-click flow: WordPress -> plugin 1.1.0 (auto-updating from then on),
 * other sites -> the new non-blocking snippet.
 * Pass `sites` when the page already has them; otherwise the component loads them.
 */
export default function InstallUpgradeNotice({ sites: sitesProp, onRecheck }) {
  const [loaded, setLoaded] = useState([]);
  const [active, setActive] = useState(null);
  const [copied, setCopied] = useState(false);
  const [snoozedUntil, setSnoozedUntil] = useState(readSnooze);

  useEffect(() => {
    if (sitesProp) return;
    api
      .get("/api/sites")
      .then(({ data }) => setLoaded(data.sites || []))
      .catch(() => {});
  }, [sitesProp]);

  const sites = sitesProp || loaded;
  const legacy = useMemo(() => sites.filter(needsInstallUpgrade), [sites]);

  if (!legacy.length || snoozedUntil > Date.now()) return null;

  const snooze = () => {
    const until = Date.now() + SNOOZE_MS;
    try {
      window.localStorage.setItem(SNOOZE_KEY, String(until));
    } catch {
      // ignore
    }
    setSnoozedUntil(until);
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const isWp = (s) => Boolean(Number(s?.install_wp)) || s?.install_method === "legacy-defer";
  const domain = active ? primaryDomain(active) : "";

  return (
    <>
      <Box
        role="region"
        aria-label="נדרש שדרוג קצר להתקנה באתר"
        sx={{
          mb: 3,
          p: { xs: 2, sm: 2.5 },
          display: "flex",
          gap: 2,
          alignItems: "flex-start",
          flexDirection: { xs: "column", sm: "row" },
          borderRadius: (t) => `${t.aw.radius.lg}px`,
          border: "1px solid",
          borderColor: (t) => alpha(t.palette.warning.main, 0.3),
          bgcolor: (t) => alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.1 : 0.06),
        }}
      >
        <IconTile tone="warning" size={40}>
          <WarningAmberRoundedIcon />
        </IconTile>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h2" sx={{ fontWeight: 700, fontSize: "0.98rem", mb: 0.5 }}>
            נדרש שדרוג קצר להתקנה באתר
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5, maxWidth: 760 }}>
            {legacy.length === 1 ? "באתר הבא מותקנת" : `ב־${legacy.length} אתרים מותקנת`} גרסה ישנה של קוד הנגישות. אם שירות
            Accss לא זמין, הגרסה הזו עלולה להאט את טעינת האתר. השדרוג לוקח כדקה, ואחריו האתר לעולם לא יושפע.
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {legacy.map((s) => (
              <Button key={s.id} variant="contained" color="warning" size="small" onClick={() => setActive(s)} sx={{ bgcolor: "warning.dark", "&:hover": { bgcolor: "warning.dark", filter: "brightness(0.92)" } }}>
                שדרג את {s.name || s.domain}
              </Button>
            ))}
            <Button size="small" onClick={snooze} sx={{ color: "text.secondary" }}>
              הזכר לי אחר כך
            </Button>
          </Stack>
        </Box>
      </Box>

      <Dialog open={!!active} onClose={() => setActive(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {active && isWp(active) ? "עדכון תוסף הוורדפרס" : "החלפת קוד ההטמעה"} · {active?.name}
        </DialogTitle>
        <DialogContent dividers>
          {active && isWp(active) ? (
            <Stack spacing={2}>
              <Typography variant="body2">
                האתר משתמש בגרסה ישנה של תוסף Accss. אחרי העדכון לגרסה 1.1.0 התוסף <b>יתעדכן מעצמו</b> בכל גרסה
                עתידית.
              </Typography>
              <Box>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>1. הורידו את הגרסה החדשה</Typography>
                <Button variant="contained" href={`${widgetServerUrl()}/accss-accessibility.zip`} download>
                  הורדת התוסף (ZIP)
                </Button>
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>2. פתחו את מסך העלאת התוספים באתר</Typography>
                <Button
                  variant="outlined"
                  href={`${domain}/wp-admin/plugin-install.php?tab=upload`}
                  target="_blank"
                  rel="noopener"
                  disabled={!domain}
                >
                  פתח את וורדפרס באתר שלי
                </Button>
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>3. העלו את הקובץ ואשרו החלפה</Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  בחרו את הקובץ שהורדתם ← ״התקן עכשיו״ ← ״החלפת הגרסה הנוכחית בגרסה שהועלתה״. ההגדרות וה־Site Key
                  נשמרים.
                </Typography>
              </Box>
            </Stack>
          ) : active ? (
            <Stack spacing={2}>
              <Typography variant="body2">
                באתר מוטמע קוד הטמעה ישן. החליפו אותו בקוד החדש — הוא נטען רק אחרי שהעמוד סיים להיטען, ולכן לעולם
                לא מאט את האתר.
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                1. מחקו מהאתר את השורה הישנה:
              </Typography>
              <Box
                component="code"
                sx={{ display: "block", p: 1.5, bgcolor: (t) => t.aw.soft, border: 1, borderColor: "divider", borderRadius: 1.5, fontSize: 12, direction: "ltr", overflowX: "auto" }}
              >
                {`<script src=".../widget/embed.js" data-site-key="${active.site_key}"></script>`}
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                2. הדביקו במקומה את הקוד החדש (לפני &lt;/body&gt;):
              </Typography>
              <TextField
                value={buildEmbedSnippet(active.site_key)}
                multiline
                minRows={4}
                fullWidth
                inputProps={{ readOnly: true, dir: "ltr", style: { fontFamily: "monospace", fontSize: 12 } }}
              />
              <Button variant="contained" onClick={() => copy(buildEmbedSnippet(active.site_key))}>
                {copied ? "הועתק ✓" : "העתק את הקוד החדש"}
              </Button>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                אם הקוד הוטמע דרך מנהל תגיות (GTM) או תוסף, עדכנו אותו שם.
              </Typography>
            </Stack>
          ) : null}
          <Alert severity="info" sx={{ mt: 2 }}>
            ההודעה תיעלם אוטומטית אחרי הביקור הבא באתר לאחר השדרוג.
          </Alert>
        </DialogContent>
        <DialogActions>
          {onRecheck ? (
            <Button
              onClick={() => {
                setActive(null);
                onRecheck();
              }}
            >
              שדרגתי, בדוק שוב
            </Button>
          ) : null}
          <Button onClick={() => setActive(null)}>סגור</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
